import { DataService } from 'src/app/services/data.service';
import { State } from './../../../../../interfaces/enums';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

import * as _ from 'lodash';
import { Router, ActivatedRoute } from '@angular/router';
import { Course, Lesson } from 'src/app/interfaces/course';
import { map, switchMap } from 'rxjs/operators';
import { of, Subject, Observable, merge } from 'rxjs';

@Component({
  templateUrl: './course.component.html',
  styleUrls: ['./course.component.scss']
})
export class CourseComponent implements OnInit {
  form: FormGroup;
  editForm: FormGroup;
  course: Course;
  lessons: Lesson[];

  State = State;

  loading = {
    lessonSubmit: false,
    lessonDelete: false
  }

  teacherForm = new FormControl('', [Validators.required]);

  teacherSearch = new Subject<string>();
  teachers$: Observable<any>;
  isAddingLesson = false;
  isEditing = false;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private data: DataService
    ) {}

  _range = (max) => Array(max).fill(1).map((x, y) => x + y);


  ngOnInit() {
    this.route.params.subscribe((p) => {
      if(p && p.courseid){
        this.getCourse(p.courseid);
      }
    })

    this.teachers$ = this.teacherSearch.pipe(
      switchMap((q) => this.data.getTeachers({q}).pipe(map((res: any) => res && res.data ? res.data : [])))
    )

    this.teacherForm.valueChanges.subscribe(v => !!v ? this.teacherSearch.next(v) : undefined);
  }

  getCourse(id){
    this.data.getCourse(id).subscribe((res) => {
      if(res && res.data){
        this.course = res.data;

        this.getLessons(id);
      }
    })
  }

  getLessons(id){
    this.data.getLessons(id).subscribe((data) => {
      this.lessons = data;

      this.isAddingLesson = false;
      this.createForm();
      this.createEditCourseForm();
    })
  }

  hasError(field){
    return this.form.get(field).invalid && this.form.get(field).touched && this.form.get(field).dirty;
  }

  createForm(){
    if(this.form){
      this.form.get('subtitle').markAsUntouched();
      this.form.get('subtitle').setValue('');
      this.form.get('credits_per_session').setValue(this.course.credits_per_session);
      this.form.get('teacher_rate_per_session').setValue(this.course.teacher_rate_per_session);
    }else{
      this.form = this.fb.group({
        teacher_id: new FormControl('', [Validators.required]),
        credits_per_session: new FormControl(this.course.credits_per_session || '', [Validators.required]),
        teacher_rate_per_session: new FormControl(this.course.teacher_rate_per_session || '', [Validators.required]),
        subtitle: new FormControl('', [Validators.required, Validators.maxLength(30)])
      })
    }
  }

  async submit(){
    try{
      this.loading.lessonSubmit = true;

      if(this.form.invalid){
        throw Error('Form is invalid.');
      }

      this.data.addLesson(this.course._id, {
        teacher_id: this.form.value.teacher_id,
        subtitle: this.form.value.subtitle,
        teacher_rate_per_session: parseFloat(this.form.value.teacher_rate_per_session as any),
        credits_per_session: parseFloat(this.form.value.credits_per_session as any)
      }).subscribe((data) => {
        if(data){
          this.getLessons(this.course._id);
        }
      })
    }catch(e){
      this.toastr.error(e.message);
    }finally{
      this.loading.lessonSubmit = false;
    }
  }

  deleteLesson(id){
    this.data.deleteLesson(this.course._id, id).subscribe(() => {
      this.getLessons(this.course._id);
    })
  }

  createEditCourseForm(){
    this.editForm = this.fb.group({
      topic: new FormControl(this.course.topic, [Validators.required]),
      description: new FormControl(this.course.description, [Validators.required]),
      session_duration: new FormControl(this.course.session_duration, [Validators.required, Validators.pattern(/^\d{1,}$/i)]),
      total_sessions: new FormControl(this.course.total_sessions, [Validators.required, Validators.pattern(/^\d{1,}$/i)]),
      max_students: new FormControl(this.course.max_students, [Validators.required, Validators.pattern(/^\d{1,}$/i)]),
      language: new FormControl(this.course.language, [Validators.required]),
      language_skill: new FormControl(this.course.language_skill, [Validators.required]),
      drawing_skill: new FormControl(this.course.drawing_skill, [Validators.required]),
      credits_per_session: new FormControl(this.course.credits_per_session, [Validators.required, Validators.pattern(/^(\d{1,})+(.\d{1,2})?$/i)]),
      teacher_rate_per_session: new FormControl(this.course.teacher_rate_per_session, [Validators.required, Validators.pattern(/^(\d{1,})+(.\d{1,2})?$/i)]),
    })
  }

  editCourse(){
    try{
      this.isEditing = true;
      this.createEditCourseForm();

    }catch(e){
      this.toastr.error(e.message);
    }
  }

  async saveCourse(){
    try{
      if(this.editForm.invalid){
        throw Error('Form is invalid.');
      }

      const {value} = this.editForm;

      value.session_duration = parseInt(value.session_duration);
      value.total_sessions = parseInt(value.total_sessions);
      value.max_students = parseInt(value.max_students);
      value.credits_per_session = parseFloat(value.credits_per_session);
      value.language_skill = parseInt(value.language_skill);
      value.drawing_skill = parseInt(value.drawing_skill);
      value.teacher_rate_per_session = parseFloat(value.teacher_rate_per_session);

      this.data.updateCourse(this.course._id, value).subscribe((res) => {
        if(res){
          this.isEditing = false;
          this.getCourse(this.course._id);
        }
      })
    }catch(e){
      this.toastr.error(e.message);
    }
  }

  async deleteCourse(){
    this.data.deleteCourse(this.course._id).subscribe((res) => {
      if(res){
        this.router.navigate(['/admin/courses']);
      }
    })
  }
}
